// Mirrors src/bds/common/types/domain.types.ts on the backend.

export enum ViewType {
  Admin = 'Admin',
  Specialist = 'Specialist',
  Communicator = 'Communicator',
  SalesLead = 'Sales Lead',
  Agent = 'Agent'
}

export enum TicketStatus {
  // V4.9.5 — NEW STATUS TAXONOMY. Mirrors backend. Legacy values kept for
  // historical row rendering (timeline events / audit log).
  Open = 'Open',

  InvestigationInProgress = 'Investigation in progress',
  InvestigationAwaitingApproval = 'Investigation in progress - awaiting approval',
  InvestigationAwaitingCx = 'Investigation in progress - awaiting CX feedback',

  ApprovedForReplacement = 'Approved for replacement',
  ApprovedReprint = 'Approved reprint',
  ApprovedReprintInProgress = 'Approved reprint in progress',
  ApprovedRefundCompleted = 'Approved refund completed',

  ReprintCompleted = 'Reprint completed',
  ReplacementCompleted = 'Replacement completed',
  Completed = 'Completed',
  Cancelled = 'Cancelled',

  // V4.9.8 — B-flow taxonomy. Vocabulary that suits sales / inquiry /
  // bug categories (B1-B6) which don't fit A-flow words like
  // "Investigation in progress" or "Approved reprint".
  InquiryReceived = 'Inquiry received',
  BInProgress = 'In progress',
  AwaitingCustomerReply = 'Awaiting customer reply',
  ResolvedCustomerAccepted = 'Resolved – customer accepted',
  ClosedCustomerDeclined = 'Closed – customer declined',
  ClosedNoCustomerResponse = 'Closed – no customer response',

  // V4.9.10 — Per-category A1-A7 status palettes. Mirrors backend.
  RefundProcessed = 'Refund Processed',
  CompletedRefunded = 'Completed – Refunded',
  InformCustomer = 'Inform Customer',
  AwaitingCustomerResponse = 'Awaiting Customer Response',
  AwaitingReturnDelivery = 'Awaiting Return Delivery',
  ReprintInProduction = 'Reprint in Production',
  ReprintShippedInformCustomer = 'Reprint Shipped – Inform Customer',
  ReplacementInProduction = 'Replacement in Production',
  ReplacementShippedInformCustomer = 'Replacement Shipped – Inform Customer',
  CallCarrierAndInformCustomer = 'Call Carrier and Inform Customer',
  AwaitingCustomerFeedback = 'Awaiting Customer Feedback',
  DeliveredCompleted = 'Delivered – Completed',

  // ─── Legacy (never offered in the UI) ─────────────────────────────
  /** @deprecated */ InProgress = 'In-progress',
  /** @deprecated */ WaitingApproval = 'Waiting Approval',
  /** @deprecated */ WaitingCustomer = 'Waiting-Customer',
  /** @deprecated */ WaitingDesign = 'Waiting-Design',
  /** @deprecated */ WaitingVendor = 'Waiting-Vendor',
  /** @deprecated */ Resolved = 'Resolved'
}

/** V4.9.5 — Statuses offered in the Change Status dropdown (order = display order). */
export const SELECTABLE_TICKET_STATUSES: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.InvestigationInProgress,
  TicketStatus.InvestigationAwaitingApproval,
  TicketStatus.InvestigationAwaitingCx,
  TicketStatus.ApprovedForReplacement,
  TicketStatus.ApprovedReprint,
  TicketStatus.ApprovedReprintInProgress,
  TicketStatus.ApprovedRefundCompleted,
  TicketStatus.ReprintCompleted,
  TicketStatus.ReplacementCompleted,
  TicketStatus.Completed,
  TicketStatus.InquiryReceived,
  TicketStatus.BInProgress,
  TicketStatus.AwaitingCustomerReply,
  TicketStatus.ResolvedCustomerAccepted,
  TicketStatus.ClosedCustomerDeclined,
  TicketStatus.ClosedNoCustomerResponse,
  TicketStatus.Cancelled
];

/**
 * V4.9.8 — A-flow statuses (A1-A7 paid-order incidents).
 */
export const A_FLOW_STATUSES: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.InvestigationInProgress,
  TicketStatus.InvestigationAwaitingApproval,
  TicketStatus.InvestigationAwaitingCx,
  TicketStatus.ApprovedForReplacement,
  TicketStatus.ApprovedReprint,
  TicketStatus.ApprovedReprintInProgress,
  TicketStatus.ApprovedRefundCompleted,
  TicketStatus.ReprintCompleted,
  TicketStatus.ReplacementCompleted,
  TicketStatus.Completed,
  TicketStatus.Cancelled
];

/**
 * V4.9.10 — CLEAN B-flow statuses (minimal helpdesk lifecycle), shared across
 * all B categories. V4.9.23 — inserted a trackable "Inform Customer" stage
 * between In progress and Completed so quotation/template/etc. tickets stay
 * visible after the product team finishes (they are NOT auto-archived until the
 * customer is informed and the ticket is marked Completed).
 * Flow: Open → In progress → Inform Customer → Completed / Cancelled.
 */
export const B_FLOW_STATUSES: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.BInProgress,
  TicketStatus.InformCustomer,
  TicketStatus.Completed,
  TicketStatus.Cancelled
];

/**
 * V4.9.8 — Statuses ONLY Admin can set. Frontend hides them from the
 * dropdown for non-Admin users. Backend also rejects API calls that try
 * to bypass — this is defence in depth.
 */
export const ADMIN_ONLY_STATUSES: TicketStatus[] = [
  TicketStatus.ApprovedForReplacement,
  TicketStatus.ApprovedReprint,
  TicketStatus.ApprovedReprintInProgress,
  TicketStatus.ApprovedRefundCompleted
];

/**
 * V4.9.10 — Per-category A1-A7 status palettes (mirrors backend).
 */
export const A1_STATUSES: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.InvestigationInProgress,
  TicketStatus.InvestigationAwaitingApproval,
  TicketStatus.RefundProcessed,
  TicketStatus.CompletedRefunded,
  TicketStatus.Cancelled
];

export const A2_STATUSES: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.InvestigationInProgress,
  TicketStatus.InformCustomer,
  TicketStatus.AwaitingCustomerResponse,
  TicketStatus.InvestigationAwaitingApproval,
  TicketStatus.AwaitingReturnDelivery,
  TicketStatus.CompletedRefunded,
  TicketStatus.Cancelled
];

export const A3_STATUSES: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.InvestigationInProgress,
  TicketStatus.AwaitingCustomerResponse,
  TicketStatus.InvestigationAwaitingApproval,
  TicketStatus.ReprintInProduction,
  TicketStatus.ReprintShippedInformCustomer,
  TicketStatus.Completed,
  TicketStatus.Cancelled
];

export const A4_STATUSES: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.InvestigationInProgress,
  TicketStatus.InformCustomer,
  TicketStatus.AwaitingCustomerResponse,
  TicketStatus.InvestigationAwaitingApproval,
  TicketStatus.ReplacementInProduction,
  TicketStatus.ReplacementShippedInformCustomer,
  TicketStatus.Completed,
  TicketStatus.Cancelled
];

export const A5_STATUSES: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.BInProgress,
  TicketStatus.InformCustomer,
  TicketStatus.Completed,
  TicketStatus.Cancelled
];

export const A6_STATUSES: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.InvestigationInProgress,
  TicketStatus.CallCarrierAndInformCustomer,
  TicketStatus.AwaitingCustomerFeedback,
  TicketStatus.DeliveredCompleted,
  TicketStatus.Cancelled
];

export const A7_STATUSES: TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.InvestigationInProgress,
  TicketStatus.InformCustomer,
  TicketStatus.AwaitingCustomerResponse,
  TicketStatus.InvestigationAwaitingApproval,
  TicketStatus.CompletedRefunded,
  TicketStatus.Cancelled
];

const A_CATEGORY_STATUSES_FE: Record<string, TicketStatus[]> = {
  A1: A1_STATUSES,
  A2: A2_STATUSES,
  A3: A3_STATUSES,
  A4: A4_STATUSES,
  A5: A5_STATUSES,
  A6: A6_STATUSES,
  A7: A7_STATUSES
};

/**
 * V4.9.10 — Returns the per-category status palette. Mirrors backend.
 */
export function statusesForCategory(categoryCode: string): TicketStatus[] {
  const code = categoryCode.toUpperCase();
  const perCat = A_CATEGORY_STATUSES_FE[code];
  if (perCat) return perCat;
  if (code.startsWith('B')) return B_FLOW_STATUSES;
  return A_FLOW_STATUSES;
}

/**
 * V4.9.13 — Friendly display labels for statuses whose stored enum value is
 * verbose. The DB value stays unchanged (no migration); this is purely how
 * the status reads in the UI. Per ops spec, the approval stage shows as
 * "Awaiting Admin Approval" everywhere instead of the long
 * "Investigation in progress - awaiting approval".
 */
const STATUS_DISPLAY_LABEL: Partial<Record<string, string>> = {
  [TicketStatus.InvestigationAwaitingApproval]: 'Awaiting Admin Approval'
};

export function statusLabel(status: TicketStatus | string): string {
  return STATUS_DISPLAY_LABEL[status as string] ?? (status as string);
}

/**
 * V4.9.13 — "Log CX" is only offered at the customer-update stages defined
 * in the ops spec — NOT at every status. Map of category → the status(es)
 * where logging a customer communication is available. A1 is absent and allows
 * it nowhere; V4.9.23 added B1–B4 at their new "Inform Customer" stage.
 *
 * Note A4's "Inform Customer" is intentionally NOT here — per spec only A4's
 * "Replacement Shipped" stage logs CX, whereas A2/A5/A7's "Inform Customer"
 * does. The gate is therefore per (category × status), not per status alone.
 */
const LOG_CX_ALLOWED_STATUSES: Record<string, TicketStatus[]> = {
  A2: [TicketStatus.InformCustomer],
  A3: [TicketStatus.ReprintShippedInformCustomer],
  A4: [TicketStatus.ReplacementShippedInformCustomer],
  A5: [TicketStatus.InformCustomer],
  A6: [TicketStatus.CallCarrierAndInformCustomer],
  A7: [TicketStatus.InformCustomer],
  // V4.9.23 — B categories log the customer communication at their new
  // "Inform Customer" stage. This also makes categoryUsesCx() true for B1–B4,
  // so the CX ("informed / not informed") badge now shows on those tickets.
  B1: [TicketStatus.InformCustomer],
  B2: [TicketStatus.InformCustomer],
  B3: [TicketStatus.InformCustomer],
  B4: [TicketStatus.InformCustomer]
};

export function canLogCx(categoryCode: string, status: TicketStatus | string): boolean {
  const list = LOG_CX_ALLOWED_STATUSES[(categoryCode ?? '').toUpperCase()];
  return !!list && list.includes(status as TicketStatus);
}

/**
 * V4.9.13 — Whether a category has ANY customer-update (Log CX) stage. Used
 * to hide the CX status badge for categories that never log CX (A1), where
 * "Never informed" would be misleading noise. As of V4.9.23 the B categories
 * (B1–B4) DO have an Inform-Customer stage, so the badge now shows for them.
 */
export function categoryUsesCx(categoryCode: string): boolean {
  return !!LOG_CX_ALLOWED_STATUSES[(categoryCode ?? '').toUpperCase()];
}

/**
 * V4.9.10 — Statuses requiring a tracking number when transitioned to.
 * Used by ChangeStatusModal to surface a required tracking_number field.
 */
export const STATUSES_REQUIRING_TRACKING_NUMBER: TicketStatus[] = [
  TicketStatus.ReprintShippedInformCustomer,
  TicketStatus.ReplacementShippedInformCustomer
];

/**
 * V4.9.10 — B1 quotation sub-types. Mirrors backend QuotationType enum.
 */
export enum QuotationType {
  Standard = 'Standard',
  Bulk = 'Bulk',
  TradeShowBoothKit = 'Trade show booth kit'
}

export const QUOTATION_TYPES = [
  QuotationType.Standard,
  QuotationType.Bulk,
  QuotationType.TradeShowBoothKit
] as const;

/**
 * V4.9.10 — Fault attribution values for the Reports module. Stored as
 * `tickets.fault_attribution` (varchar 30). Mirrors backend
 * FaultAttribution + FAULT_ATTRIBUTION_VALUES.
 */
export enum FaultAttribution {
  Sales = 'sales',
  Shipping = 'shipping',
  Vendor = 'vendor',
  Customer = 'customer',
  Design = 'design',
  InternalTeam = 'internal_team',
  None = 'none'
}

export const FAULT_ATTRIBUTION_VALUES = [
  FaultAttribution.Sales,
  FaultAttribution.Shipping,
  FaultAttribution.Vendor,
  FaultAttribution.Customer,
  FaultAttribution.Design,
  FaultAttribution.InternalTeam,
  FaultAttribution.None
] as const;

export const FAULT_ATTRIBUTION_LABELS: Record<FaultAttribution, string> = {
  [FaultAttribution.Sales]: 'Sales',
  [FaultAttribution.Shipping]: 'Shipping',
  [FaultAttribution.Vendor]: 'Vendor',
  [FaultAttribution.Customer]: 'Customer',
  [FaultAttribution.Design]: 'Design',
  [FaultAttribution.InternalTeam]: 'Internal team',
  [FaultAttribution.None]: 'No fault / unattributable'
};

export const CLOSED_TICKET_STATUSES: TicketStatus[] = [
  TicketStatus.Completed,
  TicketStatus.Cancelled,
  TicketStatus.ReprintCompleted,
  TicketStatus.ReplacementCompleted,
  TicketStatus.ApprovedRefundCompleted,
  // V4.9.8 — B-flow terminal states.
  TicketStatus.ResolvedCustomerAccepted,
  TicketStatus.ClosedCustomerDeclined,
  TicketStatus.ClosedNoCustomerResponse,
  // V4.9.10 — Per-category terminal states.
  TicketStatus.CompletedRefunded,
  TicketStatus.DeliveredCompleted,
  TicketStatus.Resolved
];

export enum ApprovalState {
  NotNeeded = 'Not needed',
  WaitingAdmin = 'Waiting Admin',
  Approved = 'Approved',
  Denied = 'Denied',
  MoreInfoNeeded = 'More info needed'
}

export enum Severity {
  P1 = 'P1',
  P2 = 'P2',
  P3 = 'P3',
  P4 = 'P4'
}

export enum CustomerMood {
  Calm = 'Calm',
  Frustrated = 'Frustrated',
  Angry = 'Angry',
  Threatening = 'Threatening'
}

export enum CommunicationMethod {
  Email = 'email',
  Call = 'call',
  Chat = 'chat',
  Whatsapp = 'whatsapp',
  Sms = 'sms',
  Other = 'other',
  Close = 'close'
}

export enum ApprovalDecision {
  Approve = 'approve',
  Deny = 'deny',
  Info = 'info'
}

// V4.9.13 — B categories consolidated to B1 Quotation, B2 Custom template,
// B3 Product doubt. "Bulk" and "Trade show booth" are B1 quotation sub-types now.
// V4.9.16 — B4 Assembly instruction added (B-flow, Sales family).
export const CATEGORY_CODES = [
  'A1',
  'A2',
  'A3',
  'A4',
  'A5',
  'A6',
  'A7',
  'B1',
  'B2',
  'B3',
  'B4'
] as const;

export type CategoryCode = (typeof CATEGORY_CODES)[number];

export const CATEGORY_LABEL: Record<CategoryCode, string> = {
  A1: 'Refund (unshipped)',
  A2: 'Refund (shipped)',
  A3: 'Reprint',
  A4: 'Replacement',
  A5: 'Assembly issue',
  A6: 'Delivery issue',
  A7: 'Chargeback',
  B1: 'Quotation',
  B2: 'Custom template',
  B3: 'Product doubt',
  B4: 'Assembly instruction'
};

/**
 * Known store codes. Treat this as a fallback only — the authoritative store
 * list comes from the backend `GET /api/v1/shopify/stores` endpoint.
 * Keep in alphabetical order so UI selects render consistently.
 */
export const KNOWN_STORE_CODES = [
  'AUS',
  'CA',
  'DE',
  'ES',
  'FR',
  'IN',
  'NZ',
  'UAE',
  'UK',
  'USA'
] as const;
export type StoreCode = string;
/** @deprecated Use the live list from `useStores()` — kept for type compatibility. */
export const STORE_CODES = KNOWN_STORE_CODES;

export const URGENCY_FLAGS = [
  'chargeback',
  'threatening_dispute',
  'angry',
  'event_72h',
  'installing_now',
  'bulk_high_value',
  'event_7days',
  'paid_upset',
  'waiting_reply_now',
  'mentioned_refund',
  'no_urgency'
] as const;
export type UrgencyFlag = (typeof URGENCY_FLAGS)[number];

/** V4.9.26 — human-friendly labels for the urgency flags. Display only; the
 *  raw snake_case value is still what gets toggled and submitted. */
export const URGENCY_FLAG_LABELS: Record<string, string> = {
  chargeback: 'Chargeback',
  threatening_dispute: 'Threatening dispute',
  angry: 'Angry customer',
  event_72h: 'Event in 72h',
  installing_now: 'Installing now',
  bulk_high_value: 'Bulk / high value',
  event_7days: 'Event in 7 days',
  paid_upset: 'Paid & upset',
  waiting_reply_now: 'Waiting on reply',
  mentioned_refund: 'Mentioned refund',
  no_urgency: 'No urgency'
};

// V4.9.26 — cost regions a solver can tag a cost against, in the Add-note
// modal. Each maps to a currency symbol; France/Spain/Germany stay distinct
// regions even though all three use the euro.
export const COST_REGIONS = [
  'UK',
  'USA',
  'CA',
  'AU',
  'NZ',
  'France',
  'Spain',
  'UAE',
  'India',
  'Germany'
] as const;
export type CostRegion = (typeof COST_REGIONS)[number];

export const COST_REGION_SYMBOL: Record<string, string> = {
  UK: '£',
  USA: '$',
  CA: 'CA$',
  AU: 'A$',
  NZ: 'NZ$',
  France: '€',
  Spain: '€',
  UAE: 'AED ',
  India: '₹',
  Germany: '€'
};

/** "£120.00" style; returns '' when region/amount missing. */
export function formatCost(region: string | null | undefined, amount: number | null | undefined): string {
  if (!region || amount == null || Number.isNaN(Number(amount))) return '';
  const sym = COST_REGION_SYMBOL[region] ?? '';
  return `${sym}${Number(amount).toFixed(2)}`;
}

export type BreachFlag = 'OK' | 'WARNING_24H' | 'WARNING_2H' | 'BREACHED' | 'Done' | 'Paused';
export type CxStatus = 'FRESH' | 'UPDATE_SOON' | 'SILENT' | 'NEVER_INFORMED';

export interface AuthenticatedUser {
  sub: string;
  name: string;
  alias: string;
  view_type: ViewType;
  geo: string | null;
  iat?: number;
  exp?: number;
}

export interface AuthLoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: {
    id: string;
    name: string;
    alias: string;
    view_type: ViewType;
    geo: string | null;
  };
}
